import axios from 'axios';

import { marketServiceInstance } from '~/domains/market/services/market.service.instance';
import { normalizeMoneylineMarket } from '~/domains/market/services/interfaces/market.types';
import type { PublicPredictionMarket } from '~/domains/market/services/interfaces/market.types';

import type {
  GetPositionsParams,
  PositionsResponse,
  PositionDetailResponse,
  PublicPosition,
} from './interfaces/positions.types';

function normalizePositionMarket<T extends { market: PublicPredictionMarket }>(position: T): T {
  return {
    ...position,
    market: normalizeMoneylineMarket(position.market),
  };
}

export const getPositionByMarketId = async (marketId: string): Promise<PublicPosition | null> => {
  try {
    const response = await marketServiceInstance.get<PositionDetailResponse>(
      `/positions/market/${marketId}`
    );

    const position = response.data.position;
    return position ? normalizePositionMarket(position) : null;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    throw error;
  }
};

export const getPositions = async (params?: GetPositionsParams): Promise<PositionsResponse> => {
  const searchParams = new URLSearchParams();

  if (params?.status) {
    searchParams.append('status', params.status);
  }

  if (params?.offset !== undefined) {
    searchParams.append('offset', params.offset.toString());
  }

  if (params?.limit !== undefined) {
    searchParams.append('limit', params.limit.toString());
  }

  const queryString = searchParams.toString();
  const url = `/positions${queryString ? `?${queryString}` : ''}`;

  const response = await marketServiceInstance.get<PositionsResponse>(url);
  const data = response.data;
  data.data = data.data.map(normalizePositionMarket);
  return data;
};

export const getPositionById = async (positionId: string): Promise<PositionDetailResponse> => {
  const response = await marketServiceInstance.get<PositionDetailResponse>(
    `/positions/${positionId}`
  );
  const data = response.data;
  data.position = normalizePositionMarket(data.position);
  return data;
};
